/**
 * Authentication API Client
 *
 * API Endpoints (matching backend paths):
 * - POST /login - Email/password login
 * - POST /register - Account creation
 * - POST /auth/firebase-login - Google/Apple OAuth via Firebase
 * - POST /logout - Logout
 * - GET /me - Get authenticated user
 * - POST /device-token - Update FCM device token
 * - POST /auth/otp/send - Send passwordless login OTP
 * - POST /auth/otp/verify - Verify OTP and authenticate
 * - POST /2fa/verify - Complete a login that hit the 2FA challenge
 * - POST /forget-password - Request OTP for password reset
 * - POST /reset-password - Reset password with OTP
 *
 * Client-side: Requests go through /api/* proxy routes which manage HttpOnly cookies.
 * Server-side: Requests go directly to backend (use server actions to set cookies).
 */

import * as Sentry from '@sentry/nextjs';
import type {
  TwoFactorRequiredResponse,
  TwoFactorVerifyPayload,
} from '@/shared/lib/auth/two-factor';
import type {
  AuthResponse,
  FirebaseLoginData,
  LoginCredentials,
  MeResponse,
  MessageResponse,
  RegisterData,
} from '../types';
import { apiClient } from './base-client';

/**
 * Every BE auth entry can answer the 2FA challenge instead of a session.
 * Narrow with `isTwoFactorRequired()` before reading `user` / `token`.
 */
type MaybeChallenged<T> = T | TwoFactorRequiredResponse;

export interface AuthenticatedMeResponse extends MeResponse {
  authenticated: boolean;
}

export interface WhopExchangeRequest {
  code: string;
  membership_id?: string | undefined;
  redirect_uri?: string | undefined;
}

export interface WhopExchangeResponse {
  message: string;
  user?: Record<string, unknown>;
  subscribed?: boolean;
  scenario?: 'new_user' | 'existing_logged_in' | 'existing_logged_out';
  requires_profile_completion?: boolean;
  email?: string;
  /** First-login signal: `null` on first authenticated session. */
  last_login?: string | null;
}

export const authClient = {
  /**
   * Login with email and password.
   * Client-side: /api/login proxy sets HttpOnly cookie.
   * Server-side: Returns token in response (caller sets cookie).
   */
  login: async (credentials: LoginCredentials): Promise<MaybeChallenged<AuthResponse>> => {
    return apiClient.post<MaybeChallenged<AuthResponse>>('/login', credentials);
  },

  /**
   * Complete a login that hit the 2FA challenge.
   * Client-side: /api/2fa/verify proxy sets the HttpOnly cookie from the real token.
   */
  twoFactorVerify: async (payload: TwoFactorVerifyPayload): Promise<AuthResponse> => {
    return apiClient.post<AuthResponse>('/2fa/verify', payload);
  },

  /**
   * Register a new account.
   * Client-side: /api/register proxy sets HttpOnly cookie.
   * Server-side: Returns token in response (caller sets cookie).
   */
  register: async (userData: RegisterData): Promise<AuthResponse> => {
    const isRedeem = !!userData.code;
    return Sentry.startSpan(
      {
        op: 'http.client',
        name: isRedeem ? 'POST /redeem-codes/register' : 'POST /register',
      },
      async (span) => {
        span?.setAttribute('flow', isRedeem ? 'redeem' : 'signup');
        return apiClient.post<AuthResponse>('/register', userData);
      }
    );
  },

  /**
   * Login with Firebase token (Google/Apple OAuth).
   * Client-side: /api/auth/firebase-login proxy sets HttpOnly cookie.
   * Server-side: Returns token in response (caller sets cookie).
   */
  firebaseLogin: async (
    firebaseData: FirebaseLoginData
  ): Promise<MaybeChallenged<AuthResponse & { requires_username?: boolean }>> => {
    return apiClient.post<MaybeChallenged<AuthResponse & { requires_username?: boolean }>>(
      '/auth/firebase-login',
      firebaseData
    );
  },

  /**
   * Send a passwordless login OTP to the user's email.
   * Client-side: /api/auth/otp/send proxy forwards to backend.
   */
  otpSend: async (email: string): Promise<MessageResponse> => {
    return apiClient.post<MessageResponse>('/auth/otp/send', { email });
  },

  /**
   * Verify a passwordless login OTP and authenticate.
   * Client-side: /api/auth/otp/verify proxy sets HttpOnly cookie.
   */
  otpVerify: async (payload: {
    email: string;
    otp: string;
  }): Promise<MaybeChallenged<AuthResponse>> => {
    return apiClient.post<MaybeChallenged<AuthResponse>>('/auth/otp/verify', payload);
  },

  /**
   * Check whether an email may check out (i.e., has no active subscription).
   * `can_checkout: false` → active subscription exists (safe to sign in).
   * `can_checkout: true`  → no active subscription (account would be orphaned
   *                         if OTP auto-creates one — redirect to /choose-plan).
   * Throws on backend errors (429, 5xx, malformed body) — the caller picks
   * the fallback: sign-in sends the code, /choose-plan opens checkout.
   */
  subscriptionCheck: async (email: string): Promise<{ can_checkout: boolean }> => {
    return apiClient.post<{ can_checkout: boolean }>('/auth/subscription-check', { email });
  },

  /**
   * Request OTP for password reset.
   * No authentication required.
   */
  forgotPassword: async (email: string): Promise<MessageResponse> => {
    return apiClient.post<MessageResponse>('/forget-password', { email });
  },

  /**
   * Reset password with OTP.
   * No authentication required.
   */
  resetPassword: async (payload: {
    email: string;
    otp: string;
    password: string;
    password_confirmation: string;
  }): Promise<MessageResponse> => {
    return apiClient.post<MessageResponse>('/reset-password', payload);
  },

  /**
   * Logout the current user.
   * Client-side: /api/logout proxy clears HttpOnly cookie.
   * Server-side: Caller clears cookie.
   */
  logout: async (device_token?: string): Promise<void> => {
    await apiClient.post('/logout', device_token ? { device_token } : undefined);
  },

  /**
   * Get the authenticated user's data.
   * Client-side: /api/me proxy reads HttpOnly cookie.
   */
  me: async (): Promise<AuthenticatedMeResponse> => {
    return apiClient.get<AuthenticatedMeResponse>('/me');
  },

  /**
   * Exchange Whop OAuth code for authentication.
   * Client-side: /api/auth/whop-exchange proxy sets HttpOnly cookie.
   */
  whopExchange: async (
    data: WhopExchangeRequest
  ): Promise<MaybeChallenged<WhopExchangeResponse>> => {
    return apiClient.post<MaybeChallenged<WhopExchangeResponse>>('/auth/whop-exchange', data);
  },

  /**
   * Register or update FCM device token for push notifications.
   */
  registerDeviceToken: async (
    device_token: string,
    platform: 'ios' | 'android' | 'web'
  ): Promise<void> => {
    await apiClient.post('/device-token', { device_token, platform });
  },
};
